import React from 'react';
import { TFunction } from 'i18next';
import {
  Chip,
  DataTableMeta,
  DateColumn,
  Typography,
  LimitedContent,
} from '@otp/ui';
import {
  Actions,
  AppArea,
  Subjects,
  User,
  WttpQuestionnaireResponseStatus,
} from 'src/graphql/generated/graphql';
import { getFullName } from 'src/utils/user-data';
import { ActionsColumn } from 'src/components/wttp/questionnaire-responses/list/table/columns/actions';
import { WttpQuestionnaireResponsesData } from '../types';
import { Status } from 'src/components/wttp/questionnaire-responses/list/table/columns/status';
import { WttpQuestionnaireResponseAction } from 'src/components/wttp/questionnaire-responses/list/table/columns/actions/actions-modals';

export const getTableConfig = (
  questionnaireResponses: WttpQuestionnaireResponsesData[],
  {
    t,
    onActionFinished,
    can,
  }: {
    t: TFunction;
    onActionFinished?: (action: WttpQuestionnaireResponseAction) => void;
    can: <TEntity>({
      action,
      appArea,
      subject,
      data,
    }: {
      action: Actions;
      appArea: AppArea;
      subject: Subjects | AppArea;
      data?: TEntity;
    }) => boolean;
  },
) => {
  const columns = [
    {
      name: 'status',
      label: t('common.status.label'),
      options: {
        customBodyRender: (
          value: WttpQuestionnaireResponseStatus,
          column: DataTableMeta,
        ) => {
          const questionnaireResponse = questionnaireResponses[column.rowIndex];

          return (
            <Status
              status={value}
              wttpQuestionnaireResponse={questionnaireResponse}
            />
          );
        },
      },
    },
    {
      name: 'questionnaire.name',
      label: t('common.name'),
      options: {
        customBodyRender: (value: string) => {
          return <LimitedContent maxRows={2}>{value}</LimitedContent>;
        },
      },
    },
    {
      name: 'store.nationalStoreNumber',
      label: t('common.store-id'),
      options: {
        customBodyRender: (value: string) => {
          return <Chip label={`#${value}`} size="small" />;
        },
      },
    },
    {
      name: 'assignedTo',
      label: t('common.assigned-to'),
      options: {
        customBodyRender: (value: User) => {
          if (!value) {
            return null;
          }

          return (
            <Typography variant="subtitle1">{getFullName(value)}</Typography>
          );
        },
      },
    },
    {
      name: 'startedAt',
      label: t('common.started-at'),
      options: {
        customBodyRender: (value: string) => {
          if (!value) {
            return null;
          }

          return <DateColumn date={value} />;
        },
      },
    },
    {
      name: 'completedAt',
      label: t('common.completed-at'),
      options: {
        customBodyRender: (value: string) => {
          if (!value) {
            return null;
          }

          return <DateColumn date={value} />;
        },
      },
    },
  ];

  if (
    can({
      action: Actions.Write,
      subject: Subjects.QuestionnaireResponses,
      appArea: AppArea.Wttp,
    }) &&
    can({
      action: Actions.Assign,
      subject: Subjects.QuestionnaireResponses,
      appArea: AppArea.Wttp,
    })
  ) {
    columns.push({
      name: 'actions',
      label: t('common.actions'),
      options: {
        customBodyRender: (_: any, column: DataTableMeta) => {
          const questionnaireResponse = questionnaireResponses[column.rowIndex];
          return (
            <ActionsColumn
              wttpQuestionnaireResponse={questionnaireResponse}
              onActionFinished={onActionFinished}
            />
          );
        },
      },
    });
  }
  return columns;
};
